import { formatDateLocal } from "@/shared/lib/date-time-performance";
import type { RehearsalSessionDTO } from "../contracts/rehearsal-sessions";

export type RehearsalSessionStatusMeta = {
  id: RehearsalSessionDTO["status"];
  label: string;
  dotClass: string;
  badgeVariant: "default" | "secondary" | "outline" | "destructive";
};

export const REHEARSAL_SESSION_STATUS: Record<
  RehearsalSessionDTO["status"],
  RehearsalSessionStatusMeta
> = {
  planned: {
    id: "planned",
    label: "ວາງແຜນ",
    dotClass: "bg-[#64748b]",
    badgeVariant: "secondary",
  },
  completed: {
    id: "completed",
    label: "ສຳເລັດ (ນັບ payout)",
    dotClass: "bg-[#16a34a]",
    badgeVariant: "default",
  },
  cancelled: {
    id: "cancelled",
    label: "ຍົກເລີກ",
    dotClass: "bg-[#dc2626]",
    badgeVariant: "destructive",
  },
};

export function formatRehearsalDate(scheduledDate: string) {
  return formatDateLocal(scheduledDate);
}

export function sortSessionsForList(sessions: RehearsalSessionDTO[]) {
  return [...sessions].sort((a, b) => {
    const dateCmp = a.scheduledDate.localeCompare(b.scheduledDate);
    if (dateCmp !== 0) return dateCmp;
    const nameA = a.performerName ?? a.performerId;
    const nameB = b.performerName ?? b.performerId;
    return nameA.localeCompare(nameB);
  });
}

export function groupSessionsByDate(sessions: RehearsalSessionDTO[]) {
  const sorted = sortSessionsForList(sessions);
  const groups: Array<{
    scheduledDate: string;
    label: string;
    sessions: RehearsalSessionDTO[];
  }> = [];

  for (const session of sorted) {
    const last = groups.at(-1);
    if (last?.scheduledDate === session.scheduledDate) {
      last.sessions.push(session);
    } else {
      groups.push({
        scheduledDate: session.scheduledDate,
        label: formatRehearsalDate(session.scheduledDate),
        sessions: [session],
      });
    }
  }

  return groups;
}

export function summarizeRehearsalDayStatus(
  sessions: RehearsalSessionDTO[],
): Record<RehearsalSessionDTO["status"], number> {
  const counts: Record<RehearsalSessionDTO["status"], number> = {
    planned: 0,
    completed: 0,
    cancelled: 0,
  };
  for (const s of sessions) {
    counts[s.status] += 1;
  }
  return counts;
}

export function hasRehearsalSessionDuplicate(
  sessions: RehearsalSessionDTO[],
  performerId: string,
  scheduledDate: string,
  excludeSessionId?: string,
) {
  return sessions.some(
    (s) =>
      s.performerId === performerId &&
      s.scheduledDate === scheduledDate &&
      s.id !== excludeSessionId,
  );
}

export function countNewRehearsalSessions(
  existingSessions: RehearsalSessionDTO[],
  performerIds: string[],
  scheduledDates: string[],
) {
  const uniquePerformers = [...new Set(performerIds)];
  const uniqueDates = [...new Set(scheduledDates)];
  let count = 0;
  for (const scheduledDate of uniqueDates) {
    for (const performerId of uniquePerformers) {
      if (
        !hasRehearsalSessionDuplicate(
          existingSessions,
          performerId,
          scheduledDate,
        )
      ) {
        count += 1;
      }
    }
  }
  return count;
}
